import { useMutation, useQueryClient } from '@tanstack/react-query';
import { login as loginService, LoginData } from '@/features/auth/services';
import { ApiResponseError } from '@/shared/types/api';
import useStorage from '@/shared/hooks/use-storage';
import { ROUTE_PATHS } from '@/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { identifyUser, clearUser } from '@/shared/services/sentry';
import { clearAllPlanogramTicketValidationState } from '@/features/planogram-tasks/hooks/use-publish-planogram-ticket-validation-event';
import { clearAllDatasetTicketValidationState } from '@/features/datasets/hooks/use-publish-qa-dataset-validation-event';

const useAuth = () => {
  const { store } = useStorage();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: async (loginData: LoginData) => {
      const res = await loginService(loginData);
      await store.setAuth(res);
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['auth-profile'] });
      identifyUser({ id: res.user.id, email: res.user.email, username: res.user.name });
    },
  });

  const logout = async (redirect?: boolean) => {
    clearUser();
    // Two features are imported here. A third should turn this into a cleanup registry.
    clearAllPlanogramTicketValidationState();
    clearAllDatasetTicketValidationState();
    await store.clear();

    navigate({
      pathname: ROUTE_PATHS.login,
      search: redirect ? `?redirect=${pathname + search}` : '',
    });
  };

  return {
    login: loginMutation.mutate,
    logout,
    isPending: loginMutation.isPending,
    isSuccess: loginMutation.isSuccess,
    error: loginMutation.error as ApiResponseError | null,
    data: loginMutation.data || null,
    isError: loginMutation.isError,
  };
};

export default useAuth;
